import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform, AnimatePresence } from "framer-motion";
import { ChevronDown, MapPin } from "lucide-react";
import { LogoMark } from "@/components/Logo";

const HERO_IMG = "/images/hero-escalade-tinted.png";

function MaskedLine({ children, delay }) {
  return (
    <span className="block overflow-hidden pb-1 pr-2">
      <motion.span
        className="block"
        initial={{ y: "115%", opacity: 0, filter: "blur(10px)" }}
        animate={{ y: "0%", opacity: 1, filter: "blur(0px)" }}
        transition={{ duration: 1.1, delay, ease: [0.22, 1, 0.36, 1] }}
      >
        {children}
      </motion.span>
    </span>
  );
}

export function Hero() {
  const ref = useRef(null);
  const [introDone, setIntroDone] = useState(false);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const bgY = useTransform(scrollYProgress, [0, 1], ["0%", "28%"]);
  const bgScale = useTransform(scrollYProgress, [0, 1], [1, 1.15]);
  const fade = useTransform(scrollYProgress, [0, 0.7], [1, 0]);

  useEffect(() => {
    const t = setTimeout(() => setIntroDone(true), 1400);
    return () => clearTimeout(t);
  }, []);

  return (
    <section ref={ref} className="relative h-[100svh] min-h-[640px] overflow-hidden grain" data-testid="hero-section">
      <AnimatePresence>
        {!introDone && (
          <motion.div
            className="fixed inset-0 z-50 bg-[#16181D] flex items-center justify-center"
            exit={{ opacity: 0, transition: { duration: 0.9, ease: "easeInOut" } }}
            data-testid="intro-curtain"
          >
            <motion.div
              initial={{ scale: 0.8, opacity: 0, rotate: -90 }}
              animate={{ scale: 1, opacity: 1, rotate: 0 }}
              transition={{ duration: 1, ease: [0.22, 1, 0.36, 1] }}
            >
              <LogoMark size={64} />
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      <motion.div className="absolute inset-0" style={{ y: bgY, scale: bgScale }}>
        <img
          src={HERO_IMG}
          alt="Chauffeur-driven luxury SUV in the South Florida sun"
          className="w-full h-full object-cover object-center"
        />
      </motion.div>
      <div className="absolute inset-0 bg-gradient-to-r from-[#16181D]/90 via-[#16181D]/45 to-transparent" />
      <div className="absolute inset-0 bg-gradient-to-t from-[#16181D] via-transparent to-[#16181D]/70" />

      <motion.div style={{ opacity: fade }} className="relative z-10 h-full max-w-7xl mx-auto px-6 lg:px-10 flex flex-col justify-end pb-24 md:pb-28">
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1.7, duration: 1 }}
          className="flex flex-wrap items-center gap-4 mb-6"
        >
          <p className="font-mono-lux text-[11px] uppercase tracking-[0.45em] text-amber-500" data-testid="hero-overline">
            Private Chauffeur Atelier
          </p>
          <span
            className="flex items-center gap-2 font-mono-lux text-[10px] uppercase tracking-[0.25em] px-3 py-1.5 border border-amber-500/30 text-amber-200/90 bg-black/30 backdrop-blur"
            data-testid="hero-location-badge"
          >
            <MapPin size={11} className="text-amber-500" /> Palm Beach · Miami · Fort Lauderdale
          </span>
        </motion.div>

        <h1 className="font-serif-lux font-light text-5xl sm:text-7xl lg:text-8xl leading-[0.95] tracking-tight text-amber-50" data-testid="hero-headline">
          <MaskedLine delay={1.5}>The Art</MaskedLine>
          <MaskedLine delay={1.7}>
            of <span className="gold-text italic font-normal">Arrival.</span>
          </MaskedLine>
        </h1>

        <motion.p
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 2.1, duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
          className="mt-8 max-w-xl text-zinc-300 text-sm sm:text-base leading-relaxed"
          data-testid="hero-subcopy"
        >
          AUREUS is South Florida's discreet chauffeur house for private clients — immaculate luxury SUVs,
          formally trained chauffeurs, and journeys composed to the minute. Airports, weddings, roadshows —
          day or night, around the clock.
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 2.3, duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
          className="mt-10 flex flex-wrap items-center gap-4"
        >
          <Link
            to="/book"
            data-testid="hero-reserve-cta"
            className="group font-mono-lux text-[11px] uppercase tracking-[0.3em] px-8 py-4 bg-amber-500 text-black hover:bg-amber-300 transition-colors duration-300"
          >
            Reserve Your Journey
          </Link>
          <a
            href="#fleet"
            data-testid="hero-fleet-cta"
            className="font-mono-lux text-[11px] uppercase tracking-[0.3em] px-8 py-4 border border-amber-500/40 text-amber-200 hover:border-amber-400 hover:text-amber-300 bg-black/20 backdrop-blur transition-colors duration-300"
          >
            Explore the Fleet
          </a>
        </motion.div>
      </motion.div>

      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 2.8 }}
        className="absolute bottom-8 right-8 lg:right-12 z-10 hidden sm:flex flex-col items-center gap-3"
      >
        <span className="font-mono-lux text-[10px] uppercase tracking-[0.4em] text-zinc-400 [writing-mode:vertical-lr]">
          Scroll
        </span>
        <ChevronDown size={16} className="text-amber-500 animate-bounce" />
      </motion.div>
    </section>
  );
}
